import {
  chartLayoutColours,
  colours,
  getColorStyles,
} from '@risksmart-app/components/src/utils/colours';
import chroma from 'chroma-js';
import type Highcharts from 'highcharts';
import { UNRATED } from 'src/pages/controls/lookupData';

import type { AxisInterval, RatingBandRegion } from './ratingBandRegions';
import type { BubbleSizeEncoding } from './riskBubbleChartSettingsSchema';
import type { RiskBubblePoint, RiskBubbleRating } from './riskBubblePoints';

export interface AxisTick {
  value: number;
  label: string;
}

export interface RiskBubbleAxis {
  title: string;
  /** One tick per configured rating value, labelled with that rating. */
  ticks: AxisTick[];
  extent: AxisInterval | undefined;
}

export interface RiskBubbleSizeInput {
  encoding: BubbleSizeEncoding;
  /**
   * What the measure is called — the same option label the settings modal
   * shows, read from the same key, so the chart cannot disagree with it.
   */
  label: string;
  formatMeasure: (value: number) => string;
}

export interface RiskBubbleChartCopy {
  /** Tooltip line labels. */
  likelihood: string;
  impact: string;
  rating: string;
  /** "Residual is worse than inherent" — keyed against the direction marker. */
  worse: string;
  /** Header for a bubble standing for several risks at one position. */
  clusterName: (count: number) => string;
}

export interface RiskBubbleChartOptionsInput {
  defaults: Highcharts.Options;
  points: RiskBubblePoint[];
  regions: RatingBandRegion[];
  likelihoodAxis: RiskBubbleAxis;
  impactAxis: RiskBubbleAxis;
  size: RiskBubbleSizeInput;
  copy: RiskBubbleChartCopy;
  formatLikelihood: (value: number) => string;
  formatImpact: (value: number) => string;
}

/** A risk a bubble stands for; a cluster bubble stands for several. */
export interface RiskBubbleMember {
  id: string;
  name: string;
}

/** What a bubble carries for the tooltip, the click handler and the caption. */
export interface RiskBubblePointCustom {
  /** The risk's id when the bubble stands for exactly one risk. */
  id: string | undefined;
  members: RiskBubbleMember[];
  likelihood: string;
  impact: string;
  rating: string;
  /** Tooltip lines after the rating: the measure, and a cluster's members. */
  extra: string;
}

export const readRiskBubbleCustom = (
  custom: unknown
): RiskBubblePointCustom | undefined =>
  typeof custom === 'object' &&
  custom !== null &&
  'members' in custom &&
  Array.isArray(custom.members)
    ? (custom as RiskBubblePointCustom)
    : undefined;

/**
 * Bands are washed out so a bubble of the same rating stays visible on top;
 * the bubbles themselves carry the palette colour at full strength.
 */
export const BAND_FILL_ALPHA = 0.35;

/**
 * The non-colour rating channel (RSP-7295): one marker shape per rating, in
 * scale order, so a monochrome print still tells the ratings apart. Size is
 * spoken for by RSP-7292 and colour alone does not survive printing; a marker
 * shape costs nothing at portfolio density. Highcharts ships these five, and
 * they repeat beyond a fifth rating, which the legend still keys by label.
 */
export const RATING_SYMBOLS = [
  'circle',
  'square',
  'diamond',
  'triangle',
  'triangle-down',
] as const;

/**
 * Unrated is hollow: white fill inside a heavier grey ring. No rated bubble is
 * hollow, so the distinction holds without colour whatever shape a rating has.
 */
export const UNRATED_MARKER: Highcharts.PointMarkerOptionsObject = {
  symbol: 'circle',
  fillColor: '#ffffff',
  lineColor: colours['light-grey'].color,
  lineWidth: 2,
};

/** Drawn on a bubble whose residual is worse than its inherent (RSP-7292). */
export const WORSE_MARK = '▲';

const paletteHex = (colour: string | undefined): string =>
  getColorStyles(colour).backgroundColor;

const bandFill = (colour: string | undefined): string => {
  const [red, green, blue] = chroma(paletteHex(colour)).rgb();

  return `rgba(${red}, ${green}, ${blue}, ${BAND_FILL_ALPHA})`;
};

const clip = (
  [from, to]: AxisInterval,
  [lower, upper]: AxisInterval
): AxisInterval => [Math.max(from, lower), Math.min(to, upper)];

const toRegionSeries = (
  region: RatingBandRegion,
  extent: { impact: AxisInterval; likelihood: AxisInterval }
): Highcharts.SeriesPolygonOptions => {
  const [x0, x1] = clip(region.impactRange, extent.impact);
  const [y0, y1] = clip(region.likelihoodRange, extent.likelihood);

  return {
    type: 'polygon',
    name: region.rating?.label ?? UNRATED.label,
    data: [
      [x0, y0],
      [x1, y0],
      [x1, y1],
      [x0, y1],
    ],
    color: bandFill(region.rating?.color ?? UNRATED.color),
    // A gap is outlined so it cannot pass for a pale rating band.
    ...(region.rating
      ? { lineWidth: 0 }
      : {
          lineWidth: 1,
          lineColor: colours['light-grey'].color,
          dashStyle: 'Dash',
        }),
    enableMouseTracking: false,
    showInLegend: false,
    includeInDataExport: false,
    zIndex: 0,
  };
};

/**
 * The z range every bubble series shares, so a size means the same in every
 * rating's series. Zero is the floor: "nothing to measure" is always the
 * smallest bubble. A single distinct value carries no information, so it is
 * pinned to the smallest size rather than left to Highcharts' mid-size default.
 */
export const bubbleZRange = (
  zs: readonly number[]
): { zMin: number; zMax: number } => {
  if (zs.length === 0) {
    return { zMin: 0, zMax: 1 };
  }
  const min = Math.min(...zs);
  const max = Math.max(...zs);
  if (min === max) {
    return { zMin: min, zMax: min + 1 };
  }

  return { zMin: 0, zMax: max };
};

const isRated = (rating: RiskBubbleRating): boolean => rating.value !== null;

/**
 * Ratings in scale order, from the bands where possible so a shape is tied to
 * a rating's place on the scale rather than to which ratings happen to be
 * plotted. A plotted rating the bands do not know is slotted in by value.
 */
const orderRatings = (
  regions: readonly RatingBandRegion[],
  points: readonly RiskBubblePoint[]
): RiskBubbleRating[] => {
  const byLabel = new Map<string, RiskBubbleRating>();
  for (const region of regions) {
    if (region.rating) {
      byLabel.set(region.rating.label, region.rating);
    }
  }
  for (const point of points) {
    if (isRated(point.rating) && !byLabel.has(point.rating.label)) {
      byLabel.set(point.rating.label, point.rating);
    }
  }

  return [...byLabel.values()].sort((a, b) => (a.value ?? 0) - (b.value ?? 0));
};

// The rating is part of the key because series are split by rating label
// before they are grouped, so two risks sharing a position but not a rating
// are drawn as two plain bubbles. Grouping them here would have the caption
// claim a count that no bubble carries.
const positionKey = (point: RiskBubblePoint): string =>
  `${point.likelihood}|${point.impact}|${point.rating.label}`;

/**
 * Exact-position overlaps become one bubble with a count (RSP-7294). Only
 * risks at precisely the same likelihood and impact *and* rating are grouped,
 * so the bubble sits exactly where every member is and matches what is drawn.
 * Nearly-coincident risks stay separate bubbles; translucency and the outline
 * keep them legible.
 */
const groupByPosition = (
  points: readonly RiskBubblePoint[]
): RiskBubblePoint[][] => {
  const groups = new Map<string, RiskBubblePoint[]>();
  for (const point of points) {
    const key = positionKey(point);
    const group = groups.get(key);
    if (group) {
      group.push(point);
    } else {
      groups.set(key, [point]);
    }
  }

  return [...groups.values()];
};

const measureText = (
  point: RiskBubblePoint,
  { size, copy }: RiskBubbleChartOptionsInput
): string | undefined =>
  point.measure === null
    ? undefined
    : `${size.formatMeasure(point.measure)}${
        point.worse ? ` ${WORSE_MARK} ${copy.worse}` : ''
      }`;

const line = (label: string, value: string): string =>
  `<br /><br /><b>${label}:</b><br />${value}`;

const memberLine = (
  member: RiskBubblePoint,
  input: RiskBubbleChartOptionsInput
): string => {
  const measure = measureText(member, input);

  return measure ? `${member.name} (${measure})` : member.name;
};

/** Tooltip lines after the rating: a cluster's members, or the measure. */
const tooltipExtra = (
  members: RiskBubblePoint[],
  input: RiskBubbleChartOptionsInput
): string => {
  if (members.length > 1) {
    return `<br /><br />${members
      .map((member) => memberLine(member, input))
      .join('<br />')}`;
  }
  const measure = measureText(members[0], input);

  return measure ? line(input.size.label, measure) : '';
};

/** What a screen reader would announce for the bubble, were the module loaded. */
const accessibleDescription = (
  members: RiskBubblePoint[],
  detail: { name: string; likelihood: string; impact: string },
  input: RiskBubbleChartOptionsInput
): string => {
  const { copy, size } = input;
  const [first] = members;
  const measure = members.length === 1 ? measureText(first, input) : undefined;

  return [
    detail.name,
    `${copy.rating}: ${first.rating.label}`,
    `${copy.likelihood}: ${detail.likelihood}`,
    `${copy.impact}: ${detail.impact}`,
    ...(members.length > 1 ? members.map((member) => member.name) : []),
    ...(measure ? [`${size.label}: ${measure}`] : []),
  ].join('. ');
};

const toBubblePoint = (
  members: RiskBubblePoint[],
  input: RiskBubbleChartOptionsInput
): Highcharts.PointOptionsObject => {
  const { copy, formatLikelihood, formatImpact } = input;
  const [first] = members;
  const isCluster = members.length > 1;
  const likelihood = formatLikelihood(first.likelihood);
  const impact = formatImpact(first.impact);
  const name = isCluster ? copy.clusterName(members.length) : first.name;
  const worse = members.some((member) => member.worse);
  const mark = `${worse ? WORSE_MARK : ''}${isCluster ? members.length : ''}`;
  const custom: RiskBubblePointCustom = {
    id: isCluster ? undefined : first.id,
    members: members.map((member) => ({ id: member.id, name: member.name })),
    likelihood,
    impact,
    rating: first.rating.label,
    extra: tooltipExtra(members, input),
  };

  return {
    x: first.impact,
    y: first.likelihood,
    // A cluster draws as large as its largest member, so no member is
    // understated; the tooltip gives each member's own measure.
    z: Math.max(...members.map((member) => member.z)),
    name,
    custom,
    ...(mark ? { dataLabels: { enabled: true, format: mark } } : {}),
    accessibility: {
      description: accessibleDescription(
        members,
        { name, likelihood, impact },
        input
      ),
    },
  };
};

interface BubbleSeriesArgs {
  rating: RiskBubbleRating;
  points: readonly RiskBubblePoint[];
  /** Position on the rating scale, which picks the marker symbol. */
  rank: number;
  legendIndex: number;
  zRange: { zMin: number; zMax: number };
  input: RiskBubbleChartOptionsInput;
}

const toBubbleSeries = ({
  rating,
  points,
  rank,
  legendIndex,
  zRange,
  input,
}: BubbleSeriesArgs): Highcharts.SeriesBubbleOptions => ({
  type: 'bubble',
  name: rating.label,
  color: paletteHex(rating.color),
  marker: isRated(rating)
    ? { symbol: RATING_SYMBOLS[rank % RATING_SYMBOLS.length] }
    : UNRATED_MARKER,
  // Larger bubbles first, so a small bubble is never buried under a big one.
  data: groupByPosition(points)
    .map((members) => toBubblePoint(members, input))
    .sort((a, b) => (b.z ?? 0) - (a.z ?? 0)),
  ...zRange,
  showInLegend: true,
  // The legend item shows the marker, keying the shape as well as the colour.
  legendSymbol: 'lineMarker',
  legendIndex,
  dataLabels: {
    enabled: false,
    inside: true,
    verticalAlign: 'middle',
    // A count or direction mark is the point, so it must not be culled as
    // overlapping something.
    allowOverlap: true,
    style: {
      fontSize: '0.625rem',
      fontWeight: '700',
    },
  },
  // A portfolio can hold more risks than Highcharts' default cap on object
  // points, which it refuses rather than plots.
  turboThreshold: 0,
  zIndex: 1,
});

/**
 * One bubble series per rating, so Highcharts' own legend keys each rating's
 * colour and shape and lets a reader hide a rating. Drawn unrated first and
 * then up the scale, so the highest ratings are never buried.
 */
const toBubbleSeriesByRating = (
  input: RiskBubbleChartOptionsInput
): Highcharts.SeriesBubbleOptions[] => {
  const { points, regions } = input;
  const zRange = bubbleZRange(points.map((point) => point.z));
  const ratings = orderRatings(regions, points);
  const byLabel = new Map<string, RiskBubblePoint[]>();
  for (const point of points) {
    const label = isRated(point.rating) ? point.rating.label : UNRATED.label;
    byLabel.set(label, [...(byLabel.get(label) ?? []), point]);
  }
  const unrated = byLabel.get(UNRATED.label);
  const rated = ratings.flatMap((rating, rank) => {
    const ratingPoints = byLabel.get(rating.label);

    return ratingPoints
      ? [
          toBubbleSeries({
            rating,
            points: ratingPoints,
            rank,
            legendIndex: rank,
            zRange,
            input,
          }),
        ]
      : [];
  });

  return [
    ...(unrated
      ? [
          toBubbleSeries({
            rating: { ...UNRATED, color: UNRATED.color },
            points: unrated,
            rank: 0,
            legendIndex: ratings.length,
            zRange,
            input,
          }),
        ]
      : []),
    ...rated,
  ];
};

const toAxis = (
  axis: RiskBubbleAxis
): Highcharts.XAxisOptions & Highcharts.YAxisOptions => {
  const labels = new Map(axis.ticks.map((tick) => [tick.value, tick.label]));

  return {
    title: {
      text: axis.title,
    },
    tickPositions: axis.ticks.map((tick) => tick.value),
    labels: {
      formatter: function formatter() {
        return labels.get(Number(this.value)) ?? String(this.value);
      },
    },
    ...(axis.extent ? { min: axis.extent[0], max: axis.extent[1] } : {}),
    startOnTick: false,
    endOnTick: false,
    minPadding: 0,
    maxPadding: 0,
    // The bands are the structure; grid lines would cut through them.
    gridLineWidth: 0,
  };
};

export const buildRiskBubbleChartOptions = (
  input: RiskBubbleChartOptionsInput
): Highcharts.Options => {
  const { defaults, regions, likelihoodAxis, impactAxis, copy } = input;
  const impactExtent = impactAxis.extent;
  const likelihoodExtent = likelihoodAxis.extent;
  // Without an extent there is no scale, and so no regions to clip.
  const bands =
    impactExtent && likelihoodExtent
      ? regions.map((region) =>
          toRegionSeries(region, {
            impact: impactExtent,
            likelihood: likelihoodExtent,
          })
        )
      : [];

  return {
    ...defaults,
    xAxis: toAxis(impactAxis),
    yAxis: toAxis(likelihoodAxis),
    legend: {
      ...defaults.legend,
      enabled: true,
      align: 'center',
      verticalAlign: 'bottom',
      layout: 'horizontal',
      symbolWidth: 10,
      symbolHeight: 10,
      itemStyle: {
        fontSize: '0.75rem',
        fontWeight: '400',
        color: chartLayoutColours.axisLabels,
      },
      // Two rows at most; Highcharts pages the rest behind arrows rather than
      // letting the key eat the plot in a small widget.
      maxHeight: 48,
    },
    tooltip: {
      ...defaults.tooltip,
      headerFormat: '',
      pointFormat:
        `<b>{point.name}</b>` +
        `<br /><br /><b>${copy.likelihood}:</b><br />{point.custom.likelihood}` +
        `<br /><br /><b>${copy.impact}:</b><br />{point.custom.impact}` +
        `<br /><br /><b>${copy.rating}:</b><br />{point.custom.rating}` +
        `{point.custom.extra}`,
    },
    series: [...bands, ...toBubbleSeriesByRating(input)],
  };
};
